/**
 * User-Centric Permission System
 *
 * This module defines granular capabilities that map to features in HELM.
 * Each user has a single configuration object defining their capabilities,
 * default page, and any special flags.
 *
 * Permission Naming Convention:
 * - Format: `domain.action` (e.g., 'orders.view', 'orders.edit')
 * - Use '*' for superuser access (all permissions)
 * - Domains correspond to feature areas in the application
 *
 * Data Source:
 * - This file is the source of truth for who may do what in HELM.
 * - Cloud Functions cannot import this TypeScript module, so
 *   `functions/src/permissions/inCodeUserPermissions.js` holds a generated
 *   snapshot. After changing presets or `userPermissions`, run:
 *     npm run sync:permissions
 *   `npm test` fails if the snapshot is stale.
 * - AuthProvider and usePermissions() read this map at runtime.
 */

/**
 * All available permissions in the system.
 *
 * Organised by domain:
 * - orders: Sales order management
 * - schedule: Production schedule views
 * - router: Shop router and work order management
 * - inventory: Inventory management
 * - purchasing: Purchase orders, vendors, receivers
 * - customers: Customer management and pricing
 * - qa: Quality assurance and non-conformance
 * - shipments: Shipment management
 * - admin: Administrative tools and reports
 * - parts: Part finder and pricing tools
 * - part-quotes: Part quotation workflow
 */
export type Permission =
  | "*"
  // Orders
  | "orders.view"
  | "orders.edit"
  | "orders.delete"
  | "orders.combine"
  // Work Orders
  | "work-orders.edit"
  // Production Schedule
  | "schedule.view"
  | "schedule.edit"
  | "schedule.complete"
  // Router & Work Orders
  | "router.view"
  | "router.edit"
  | "router.scan"
  // Inventory
  | "inventory.view"
  | "inventory.edit"
  | "inventory.delete"
  // Purchasing
  | "purchasing.view"
  | "purchasing.edit"
  | "purchasing.vendors"
  | "purchasing.receivers"
  // Customers
  | "customers.view"
  | "customers.edit"
  | "customers.pricing"
  // Quality Assurance
  | "qa.view"
  | "qa.edit"
  | "qa.exceptions"
  // Shipments
  | "shipments.view"
  | "shipments.edit"
  // Admin
  | "admin.reports"
  | "admin.utilities"
  | "admin.settings"
  | "admin.qbo-import"
  | "admin.vacation-calendar"
  | "admin.period-closing"
  | "env.toggle"
  // Parts & Product Development
  | "parts.view"
  | "parts.edit"
  | "parts.dev-queue"
  | "parts.terms"
  | "parts.pd-tools"
  // Part Quotes
  | "part-quotes.view"
  | "part-quotes.edit"
  | "part-quotes.delete"
  | "part-quotes.copy"
  | "part-quotes.convert"
  // Customer portal
  | "customer-portal.view";

/**
 * User permission configuration
 */
export interface UserPermissions {
  /** List of permissions granted to this user */
  permissions: Permission[];
  /** Permissions explicitly denied even if '*' is present */
  restrictions?: Permission[];
  /** Default landing page after login */
  defaultPage: string;
  /** Optional display name for UI purposes */
  displayName?: string;
}

/**
 * Permission presets for common role combinations.
 * These can be spread into user configurations for convenience.
 */
export const permissionPresets = {
  /** Full administrative access (everything except superuser-only features) */
  fullAdmin: [
    "env.toggle",
    "orders.view",
    "orders.edit",
    "orders.delete",
    "orders.combine",
    "schedule.view",
    "schedule.edit",
    "schedule.complete",
    "router.view",
    "router.edit",
    "inventory.view",
    "inventory.edit",
    "inventory.delete",
    "purchasing.view",
    "purchasing.edit",
    "purchasing.vendors",
    "purchasing.receivers",
    "customers.view",
    "customers.edit",
    "customers.pricing",
    "qa.view",
    "qa.edit",
    "qa.exceptions",
    "shipments.view",
    "shipments.edit",
    "admin.reports",
    "admin.utilities",
    "admin.vacation-calendar",
    "admin.period-closing",
    "parts.view",
    "parts.edit",
    "parts.dev-queue",
    "parts.terms",
    "work-orders.edit",
  ] as Permission[],

  /** Supervisor-level access (production floor management) */
  supervisor: [
    "orders.view",
    "orders.edit",
    "schedule.view",
    "schedule.edit",
    "schedule.complete",
    "router.view",
    "router.edit",
    "router.scan",
    "inventory.view",
    "inventory.edit",
    "purchasing.view",
    "purchasing.receivers",
    "qa.view",
    "qa.edit",
    "parts.view",
    "admin.vacation-calendar",
  ] as Permission[],

  /** Shop floor user access */
  shopUser: [
    "orders.view",
    "schedule.view",
    "router.view",
    "router.scan",
    "inventory.view",
    "purchasing.view",
    "purchasing.receivers",
    "qa.view",
    "qa.edit",
    "admin.vacation-calendar",
  ] as Permission[],

  /** Scanner station access (minimal, focused on scanning) */
  scanner: ["router.view", "router.scan", "qa.view"] as Permission[],

  /** Customer portal access */
  customer: [
    "parts.view",
    "customer-portal.view",
  ] as Permission[],

  /** Shipping department access */
  shipping: [
    "schedule.view",
    "schedule.complete",
    "orders.view",
    "shipments.view",
    "shipments.edit",
    "admin.vacation-calendar",
  ] as Permission[],
} as const;

/**
 * Seed source for the Firestore permissions document at helm-settings/permissions.
 *
 * This map is NO LONGER the runtime source of truth — permissions are fetched
 * from Firestore on login (see AuthProvider).  This object is kept in source
 * code as:
 *   1. The initial seed for a fresh Firestore environment.
 *   2. A code-level reference / changelog of who has what access.
 *   3. A fallback when Firestore is unreachable during development.
 *
 * To seed Firestore from this map, call `seedPermissionsIfAbsent()` from
 * src/services/db/permissions.ts.
 */
export const userPermissions: Record<string, UserPermissions> = {
  // ==================== SUPER ADMIN ====================
  "keithh@wildwoodmfg.com": {
    permissions: ["*"],
    restrictions: ["customer-portal.view"],
    defaultPage: "/",
    displayName: "Keith H",
  },

  // ==================== ADMIN USERS ====================
  "bruceh@wildwoodmfg.com": {
    permissions: [...permissionPresets.fullAdmin, "orders.combine"],
    defaultPage: "/",
    displayName: "Bruce H",
  },
  "cathyh@wildwoodmfg.com": {
    permissions: [...permissionPresets.fullAdmin, "orders.combine"],
    defaultPage: "/",
    displayName: "Cathy H",
  },
  "darrans@wildwoodmfg.com": {
    permissions: [...permissionPresets.fullAdmin],
    defaultPage: "/",
    displayName: "Darran S",
  },
  "christineh@wildwoodmfg.com": {
    permissions: [...permissionPresets.fullAdmin, "orders.combine"],
    defaultPage: "/",
    displayName: "Christine H",
  },
  "blaker@wildwoodmfg.com": {
    permissions: [...permissionPresets.fullAdmin, "orders.combine"],
    defaultPage: "/",
    displayName: "Blake R",
  },
  "edwardl@wildwoodmfg.com": {
    permissions: [
      "orders.view",
      "schedule.view",
      "router.view",
      "router.scan",
      "inventory.view",
      "inventory.edit",
      "purchasing.view",
      "purchasing.edit",
      "purchasing.vendors",
      "purchasing.receivers",
      "qa.view",
      "shipments.view",
      "shipments.edit",
      "parts.view",
      "parts.edit",
      "admin.reports",
      "admin.settings",
      "customers.view",
      "customers.pricing",
    ],
    defaultPage: "/",
    displayName: "Edward L",
  },

  // ==================== SUPERVISORS ====================
  "nicholas@wildwoodmfg.com": {
    permissions: [
      ...permissionPresets.supervisor,
      ...permissionPresets.shopUser,
      "shipments.view",
      "parts.pd-tools",
    ],
    defaultPage: "/",
    displayName: "Nicholas",
  },
  "stevej@wildwoodmfg.com": {
    permissions: [
      ...permissionPresets.supervisor,
      "work-orders.edit",
      "orders.combine",
    ],
    defaultPage: "/",
    displayName: "Steve J",
  },
  "brianr@wildwoodmfg.com": {
    permissions: [...permissionPresets.supervisor],
    defaultPage: "/",
    displayName: "Brian R",
  },

  // ==================== SHOP USERS ====================
  "timeclock@wildwoodmfg.com": {
    permissions: [...permissionPresets.shopUser],
    defaultPage: "/",
    displayName: "Timeclock",
  },

  // ==================== SHIPPING ====================
  "shipping@wildwoodmfg.com": {
    permissions: [...permissionPresets.shipping],
    defaultPage: "/",
    displayName: "Shipping",
  },

  // ==================== SCANNER STATIONS ====================
  "station-assembly@wildwoodmfg.com": {
    permissions: [...permissionPresets.scanner],
    defaultPage: "/scanner",
    displayName: "Assembly Station",
  },
  "station-cnc@wildwoodmfg.com": {
    permissions: [...permissionPresets.scanner],
    defaultPage: "/scanner",
    displayName: "CNC Station",
  },
  "station-construction-jason@wildwoodmfg.com": {
    permissions: [...permissionPresets.scanner],
    defaultPage: "/scanner",
    displayName: "Construction Station (Jason)",
  },
  "station-construction@wildwoodmfg.com": {
    permissions: [...permissionPresets.scanner],
    defaultPage: "/scanner",
    displayName: "Construction Station",
  },
  "station-dryroom@wildwoodmfg.com": {
    permissions: [...permissionPresets.scanner],
    defaultPage: "/scanner",
    displayName: "Dry Room Station",
  },
  "station-finishing@wildwoodmfg.com": {
    permissions: [...permissionPresets.scanner],
    defaultPage: "/scanner",
    displayName: "Finishing Station",
  },
  "station-qa-shipping@wildwoodmfg.com": {
    permissions: [...permissionPresets.scanner],
    defaultPage: "/scanner",
    displayName: "QA/Shipping Station",
  },
  "station-release@wildwoodmfg.com": {
    permissions: [...permissionPresets.scanner],
    defaultPage: "/scanner",
    displayName: "Release Station",
  },

  // ==================== CUSTOMER ACCOUNTS ====================
  "bloomdoom@wildwoodmfg.com": {
    permissions: [...permissionPresets.customer],
    defaultPage: "/pricing",
    displayName: "Bloom Doom",
  },
  "eomar@wildwoodmfg.com": {
    permissions: [...permissionPresets.customer],
    defaultPage: "/pricing",
    displayName: "E. & O. Mari, Inc.",
  },
  "eggle@wildwoodmfg.com": {
    permissions: [...permissionPresets.customer],
    defaultPage: "/pricing",
    displayName: "Eggle",
  },
  "friedman@wildwoodmfg.com": {
    permissions: [...permissionPresets.customer],
    defaultPage: "/pricing",
    displayName: "Friedman",
  },
  "gould@wildwoodmfg.com": {
    permissions: [...permissionPresets.customer],
    defaultPage: "/pricing",
    displayName: "Gould",
  },
  "jones@wildwoodmfg.com": {
    permissions: [...permissionPresets.customer],
    defaultPage: "/pricing",
    displayName: "Jones",
  },
  "kline@wildwoodmfg.com": {
    permissions: [...permissionPresets.customer],
    defaultPage: "/pricing",
    displayName: "Kline",
  },
  "lullcustom@wildwoodmfg.com": {
    permissions: [...permissionPresets.customer],
    defaultPage: "/pricing",
    displayName: "Lull Custom",
  },
  "luxxtone@wildwoodmfg.com": {
    permissions: [...permissionPresets.customer],
    defaultPage: "/pricing",
    displayName: "Luxxtone",
  },
  "mjt@wildwoodmfg.com": {
    permissions: [...permissionPresets.customer],
    defaultPage: "/pricing",
    displayName: "MJT",
  },
  "nash@wildwoodmfg.com": {
    permissions: [...permissionPresets.customer],
    defaultPage: "/pricing",
    displayName: "Nash Guitars",
  },
  "nybw@wildwoodmfg.com": {
    permissions: [...permissionPresets.customer],
    defaultPage: "/pricing",
    displayName: "NYBW",
  },
  "prosounds@wildwoodmfg.com": {
    permissions: [...permissionPresets.customer],
    defaultPage: "/pricing",
    displayName: "Prosound",
  },
  "rnrr@wildwoodmfg.com": {
    permissions: [...permissionPresets.customer],
    defaultPage: "/pricing",
    displayName: "Rock 'N Roll Relics",
  },
  "sadowsky@wildwoodmfg.com": {
    permissions: [...permissionPresets.customer],
    defaultPage: "/pricing",
    displayName: "Sadowsky",
  },
  "shabat@wildwoodmfg.com": {
    permissions: [...permissionPresets.customer],
    defaultPage: "/pricing",
    displayName: "Shabat",
  },
  "shelton@wildwoodmfg.com": {
    permissions: [...permissionPresets.customer],
    defaultPage: "/pricing",
    displayName: "Shelton",
  },
  "trussart@wildwoodmfg.com": {
    permissions: [...permissionPresets.customer],
    defaultPage: "/pricing",
    displayName: "Trussart",
  },
  "xotic@wildwoodmfg.com": {
    permissions: [...permissionPresets.customer],
    defaultPage: "/pricing",
    displayName: "Xotic",
  },
};

/**
 * Synchronous lookup from the in-code map.
 */
export const getUserPermissions = (
  email: string | null | undefined,
): UserPermissions | null => {
  if (!email) return null;
  return userPermissions[email.toLowerCase()] ?? null;
};

/**
 * Helper function to check if a user has a specific permission.
 * Handles the '*' wildcard for superusers.
 * Explicit restrictions override even the '*' wildcard.
 */
export const checkPermission = (
  userPerms: Permission[],
  required: Permission | Permission[],
  restrictions?: Permission[],
): boolean => {
  const requiredArray = Array.isArray(required) ? required : [required];

  // Explicit restrictions override everything, including superuser '*'
  if (restrictions && restrictions.length > 0) {
    const isRestricted = requiredArray.some((p) => restrictions.includes(p));
    if (isRestricted) return false;
  }

  if (userPerms.includes("*")) return true;
  return requiredArray.some((p) => userPerms.includes(p));
};

/**
 * Helper function to check if a user has ALL of the specified permissions.
 * Handles the '*' wildcard for superusers.
 * Explicit restrictions override even the '*' wildcard.
 */
export const checkAllPermissions = (
  userPerms: Permission[],
  required: Permission[],
  restrictions?: Permission[],
): boolean => {
  // Explicit restrictions override everything, including superuser '*'
  if (restrictions && restrictions.length > 0) {
    const isRestricted = required.some((p) => restrictions.includes(p));
    if (isRestricted) return false;
  }

  if (userPerms.includes("*")) return true;
  return required.every((p) => userPerms.includes(p));
};

/**
 * Get all users who have a specific permission.
 * Queries the in-code seed map — useful for tooling / admin scripts.
 */
export const getUsersWithPermission = (permission: Permission): string[] => {
  return Object.entries(userPermissions)
    .filter(
      ([_, config]) =>
        config.permissions.includes("*") ||
        config.permissions.includes(permission),
    )
    .map(([email]) => email);
};

/**
 * Get all users who have ANY of the specified permissions.
 * Queries the in-code seed map — useful for tooling / admin scripts.
 */
export const getUsersWithAnyPermission = (
  permissions: Permission[],
): string[] => {
  return Object.entries(userPermissions)
    .filter(
      ([_, config]) =>
        config.permissions.includes("*") ||
        permissions.some((p) => config.permissions.includes(p)),
    )
    .map(([email]) => email);
};

/** Permissions that grant the consumeMaterials / advance-runner call. */
export const CONSUME_MATERIALS_PERMISSIONS: Permission[] = [
  "router.scan",
  "router.edit",
];

/**
 * Same grant the consumeMaterials Cloud Function uses: in-code map,
 * router.scan or router.edit (or *).
 */
export const callerMayConsumeMaterials = (
  email: string | null | undefined,
): boolean => {
  const config = getUserPermissions(email);
  if (!config) return false;
  return checkPermission(
    config.permissions,
    CONSUME_MATERIALS_PERMISSIONS,
    config.restrictions,
  );
};
